"use client";

/**
 * 게시글 읽음 표시 hook (마이그 101 후속, 2026-05-25).
 *
 * localStorage 기반 (디바이스별). DB 부담 X.
 * 최대 500개 유지 (LRU — 가장 오래된 것 자동 제거).
 */
import { useCallback, useEffect, useState } from "react";

const KEY = "trendrain_community_read_posts";
const MAX = 500;

function load(): Set<number> {
  if (typeof window === "undefined") return new Set();
  try {
    const raw = window.localStorage.getItem(KEY);
    if (!raw) return new Set();
    const arr = JSON.parse(raw);
    if (Array.isArray(arr)) return new Set(arr.filter((n) => typeof n === "number"));
  } catch { /* corrupt — reset */ }
  return new Set();
}

function persist(set: Set<number>) {
  if (typeof window === "undefined") return;
  try {
    const arr = Array.from(set);
    // LRU 가드 — 500 초과 시 앞에서 자름
    const trimmed = arr.length > MAX ? arr.slice(arr.length - MAX) : arr;
    window.localStorage.setItem(KEY, JSON.stringify(trimmed));
  } catch { /* quota — silent */ }
}

export function useReadPosts() {
  const [readSet, setReadSet] = useState<Set<number>>(() => new Set());

  useEffect(() => {
    setReadSet(load());
  }, []);

  const markRead = useCallback((postId: number) => {
    setReadSet((prev) => {
      if (prev.has(postId)) return prev;
      const next = new Set(prev);
      next.delete(postId);   // LRU touch
      next.add(postId);
      persist(next);
      return next;
    });
  }, []);

  const isRead = useCallback((postId: number) => readSet.has(postId), [readSet]);

  return { isRead, markRead };
}
